import { ICONS } from 'components/Icon';
import { NetworkError } from 'lib/errors';
import { isCancelled, isError, isNetworkError } from 'lib/isError';
import { apiGet, apiPost, apiRemove } from 'service/api';
import * as api from 'service/api/constants';
import { clearStorage } from 'service/storage';
import {
  ALERT_ERROR,
  HIDE_BLOCKING_LOADER,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  SHOW_BLOCKING_LOADER,
  doShowLoading,
  doShowSyncModal,
  eventSelectScreenCancel,
  eventSelectScreenSelection,
} from 'store/app/actions';
import {
  AUTHENTICATE,
  doAuthHideAuthModal,
  doAuthShowAuthModal,
  doSettingOrganisationSelectStart,
  doUserOrganisationSet,
  REQUEST_PASSWORD_RESET,
  SIGN_OUT,
} from 'store/auth/actions';
import { ApiResponseAuth, AuthenticationSteps } from 'store/auth/types';
import { CATCH_ERROR, ERROR_FIRE_ACTION } from 'store/error/actions';
import {
  selectSyncHasPendingDataSync,
  selectSyncStatus,
  selectSyncFileUploadQueueCount,
} from 'store/sync/selectors';
import { call, put, race, select, take, takeEvery } from 'typed-redux-saga';

import { ModalScreens } from 'components/Navigation/types';
import { theme } from 'lib/theme';
import { sagaConfirm, sagaWaitForCancel } from 'store/sagaHelpers';
import { pluralise } from 'lib/pluralise';
import { filterArrayEmpty, getScreenSize, randomKey } from 'lib/riskbase';
import {
  PAUSE_BG_NETWORK,
  UNPAUSE_BG_NETWORK,
  doSetupOrganisationOpen,
} from 'store/actions';
import {
  EPOrganisationList,
  EPOrganisationBasic,
} from 'types/Endpoints/Organisation';
import {
  systemEventSetupOrganisationNotOpened,
  systemEventSetupOrganisationOpened,
} from 'store/ActionsEvents';
import { ScreenSize } from 'lib/constants';
import { setUser as sentrySetUser } from '@sentry/react-native';
import delay from 'delay';
import {
  selectAuthStep,
  selectAuthSignInAs,
  selectAuthStepToken,
  selectHasAuthToken,
  selectUserSelectedOrganisationId,
} from './selectors';

export const authSideEffects = [
  takeEvery(AUTHENTICATE.request, function* f(action) {
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const authenticationStep = yield* select(selectAuthStep);
      const http = {
        [AuthenticationSteps.email]: api.AUTH_EMAIL,
        [AuthenticationSteps.emailvalidate]: api.AUTH_EMAIL_VALIDATE,
        [AuthenticationSteps.password]: api.AUTH_PASSWORD,
        [AuthenticationSteps.setpassword]: api.AUTH_SET_PASSWORD,
        [AuthenticationSteps.tfaSetup]: api.AUTH_TFA_SETUP,
        [AuthenticationSteps.tfa]: api.AUTH_TFA,
        [AuthenticationSteps.authenticated]: api.AUTH_EMAIL,
      }[authenticationStep];
      const payload = {
        ...action.payload,
        token: (yield* select(selectAuthStepToken)) || null,
        as: yield* select(selectAuthSignInAs),
        organisation: yield* select(selectUserSelectedOrganisationId),
      };
      const response = yield* apiPost<ApiResponseAuth>(http, payload);
      yield* put(AUTHENTICATE.success(response));
      if (response.step === AuthenticationSteps.authenticated) {
        sentrySetUser({
          id: response.data.user.id,
        });
        yield* put(ERROR_FIRE_ACTION());
        yield* put(doAuthHideAuthModal());
        yield* put(
          doUserOrganisationSet({ organisation: response.data.organisation }),
        );
      }
    } catch (error) {
      if (isNetworkError(error)) {
        if (
          error.message === 'ClientOutOfDateMinor' ||
          error.message === 'ClientOutOfDateMajor'
        ) {
          yield* put(
            NAVIGATE_MODAL({
              screen: ModalScreens.UpgradeRequired,
              params: {
                major: error.message === 'ClientOutOfDateMajor',
                withClose: yield* select(selectHasAuthToken),
              },
            }),
          );
        } else {
          yield* put(ALERT_ERROR({ error }));
        }
        yield* put(AUTHENTICATE.failure(error));
      } else {
        yield* put(CATCH_ERROR({ error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),
  takeEvery(REQUEST_PASSWORD_RESET.request, function* f() {
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiPost<ApiResponseAuth>(
        api.AUTH_REQUEST_PASSWORD_RESET,
        {
          token: (yield* select(selectAuthStepToken)) || null,
        },
      );
      yield* put(REQUEST_PASSWORD_RESET.success(response));
    } catch (error) {
      if (isNetworkError(error)) {
        yield* put(REQUEST_PASSWORD_RESET.failure(error));
        yield* put(ALERT_ERROR({ error }));
      } else {
        yield* put(CATCH_ERROR({ error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),
  takeEvery(SIGN_OUT.request, function* f() {
    yield* put(PAUSE_BG_NETWORK());

    // Ensure sync has finished otherwise sync could finish after
    // the user has signed out, causing the signed out user to have data
    let wasCancelled = false;
    try {
      const waitKey = randomKey();
      yield* put(
        doShowLoading({
          text: 'Sign Out\nWaiting for Sync',
          waitKey,
        }),
      );
      yield* race([
        (function* f1() {
          let checkAgain = true;
          do {
            const syncStatus = yield* select(selectSyncStatus);
            if (syncStatus.step === 'waiting') {
              checkAgain = false;
            } else {
              yield delay(500);
            }
          } while (checkAgain);
        })(),
        sagaWaitForCancel(waitKey),
      ]);
      yield* put(HIDE_BLOCKING_LOADER());
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isCancelled(error)) {
        wasCancelled = true;
      }
    }

    if (wasCancelled) {
      yield* put(UNPAUSE_BG_NETWORK());
      return;
    }

    const fileUploadQueueCount = yield* select(selectSyncFileUploadQueueCount);
    const pendingDataSync = yield* select(selectSyncHasPendingDataSync);

    try {
      // yield* put(PAUSE_BG_NETWORK());
      if (fileUploadQueueCount || pendingDataSync) {
        const sagaConfirmConf = {
          confirmText: 'Sign Out',
          cancelText: 'Cancel',
          destructive: true,
          delayedConfirm: true,
          text:
            "Changes you've made have not\nbeen uploaded to RiskBase yet\n\n" +
            '*You will delete your changes if*\n*you sign out before syncing*\n\n' +
            `You have ${[
              pendingDataSync ? '*data changes*' : '',
              pluralise(fileUploadQueueCount, '*{x} file*', '*{x} files*', ''),
            ]
              .filter(filterArrayEmpty)
              .join(' and ')} to upload`,
        };
        // Confirm sign out with local changes
        const outcome1 = yield* sagaConfirm({
          ...sagaConfirmConf,
          titleIcon: ICONS.CircleExclamationLight,
          titleIconColor: theme.colors.red,
        });
        if (!outcome1) {
          yield* put(UNPAUSE_BG_NETWORK());
          yield* put(doShowSyncModal({ downloadCleanData: false }));
          return;
        }
        // Delay is required for confirm modal to close and re-open
        yield delay(1);
        // Confirm a second time
        const outcome2 = yield* sagaConfirm({
          ...sagaConfirmConf,
          title: 'Please Confirm Again\nYou Will Lose Data',
          titleColor: theme.colors.red,
        });
        if (!outcome2) {
          yield* put(UNPAUSE_BG_NETWORK());
          yield* put(doShowSyncModal({ downloadCleanData: false }));
          return;
        }
      } else if (
        !(yield* sagaConfirm({
          cancelText: 'Cancel',
          confirmText: 'Sign Out',
          destructive: true,
          text: 'Are you sure you want to sign out of RiskBase?',
        }))
      ) {
        yield* put(UNPAUSE_BG_NETWORK());
        return;
      }

      // The user has confirmed signing out.

      yield* put(SHOW_BLOCKING_LOADER());
      try {
        yield* call(apiRemove, api.AUTH_SIGN_OUT);
      } catch (error) {
        // We don't mind if the server couldn't be contacted to sign out
        // Supress network errors, but error with any other issues
        if (!(error instanceof NetworkError)) {
          throw error;
        }
      }
      yield* call(clearStorage);
      yield* put(SIGN_OUT.success());
      yield* put(HIDE_BLOCKING_LOADER());
      yield* put(UNPAUSE_BG_NETWORK());
      yield* put(doAuthShowAuthModal({ actionAfterAuth: undefined }));
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
      yield* put(CATCH_ERROR({ error }));
      yield* put(UNPAUSE_BG_NETWORK());
      yield* put(HIDE_BLOCKING_LOADER());
    }
  }),
  takeEvery(doSettingOrganisationSelectStart, function* f(action) {
    const currentOrganisationId = yield* select(
      selectUserSelectedOrganisationId,
    );
    // Get list of available organisations
    let listLoaded = false;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiGet<EPOrganisationList['response']>(
        api.apiSettingsOrganisationList(),
      );
      listLoaded = true;
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(
        NAVIGATE_MODAL({
          screen: ModalScreens.SELECT,
          params: {
            options: response.organisations.map((o) => ({
              name: o.name,
              internalValue: o.id,
            })),
            value: currentOrganisationId ? [currentOrganisationId] : [],
            eventKey: randomKey(),
            searchable: true,
            title: 'Your Organisations',
            closeOnDone: false,
            hideRadioButton: true,
          },
        }),
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    if (!listLoaded) {
      return;
    }
    let finish = false;
    do {
      const yielded = yield* take([
        eventSelectScreenSelection,
        eventSelectScreenCancel,
      ]);
      if (yielded.type === eventSelectScreenSelection.toString()) {
        const typedYielded = yielded as ReturnType<
          typeof eventSelectScreenSelection
        >;
        const newOrganisationId = typedYielded.payload.stringValues[0];
        if (newOrganisationId) {
          if (getScreenSize().size === ScreenSize.small) {
            // Small screen: load basic details
            yield* put(SHOW_BLOCKING_LOADER());
            try {
              const { organisation } = yield* apiGet<
                EPOrganisationBasic['response']
              >(api.apiSettingsOrganisationBasic(newOrganisationId));
              yield* put(doUserOrganisationSet({ organisation }));
              finish = true;
            } catch (error) {
              if (isError(error)) {
                yield* put(ALERT_ERROR({ action, error }));
              }
            }
            yield* put(HIDE_BLOCKING_LOADER());
          } else {
            // Large screen: load and open organisation's profile
            yield* put(
              doSetupOrganisationOpen({
                newOrganisationId,
              }),
            );
            if (
              (yield* take([
                systemEventSetupOrganisationOpened,
                systemEventSetupOrganisationNotOpened,
              ])).type === systemEventSetupOrganisationOpened.toString()
            ) {
              finish = true;
            }
          }
        } else {
          finish = true;
        }
        if (finish) {
          yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SELECT]));
        }
      } else {
        finish = true;
      }
    } while (!finish);
  }),
];
